import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { compressImage } from "../lib/imageCompression";
import { FileText, Receipt, Paperclip, Upload, Trash2, Loader2, Download } from "lucide-react";
import { useConfirm } from "./ConfirmProvider";

/**
 * Multi-file attachments for a payment stage.
 * Used by both the ID firm (PaymentsTab) and the homeowner (CustomerProject).
 *
 * Backend endpoints:
 *   POST   /payment-stages/{id}/documents      (multipart: file, type, label)
 *   GET    /payment-stages/{id}/documents/{doc_id}
 *   DELETE /payment-stages/{id}/documents/{doc_id}
 */
const TYPE_META = {
  invoice: { label: "Invoice", icon: FileText, tint: "bg-[#E0E7FF] text-[#3730A3]" },
  receipt: { label: "Receipt", icon: Receipt, tint: "bg-[#DCFCE7] text-[#166534]" },
  other: { label: "Document", icon: Paperclip, tint: "bg-[#F7F4EC] text-[#7A7571]" },
};

function fmtSize(bytes) {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function PaymentStageDocuments({
  stageId,
  documents = [],
  currentUserId,
  currentUserRole,
  onChange,
}) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [pickerType, setPickerType] = useState("other");
  const canManageAll = ["admin", "pm", "designer"].includes(currentUserRole);
  // Homeowners have read-only access — they can view/download files uploaded
  // by the ID firm but cannot upload or delete anything.
  const canUpload = canManageAll;
  const canDeleteAny = canManageAll;
  const confirm = useConfirm();

  const startUpload = (t) => {
    setPickerType(t);
    inputRef.current && inputRef.current.click();
  };

  const onFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      // Compress phone photos before send. Invoices/receipts uploaded as
      // PDFs (or any non-image) pass through untouched.
      const shrunk = await compressImage(file);
      const fd = new FormData();
      fd.append("file", shrunk);
      fd.append("type", pickerType);
      fd.append("label", file.name);
      await api.post(`/payment-stages/${stageId}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${TYPE_META[pickerType]?.label || "Document"} uploaded`);
      onChange && (await onChange());
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = async (doc) => {
    const ok = await confirm({
      title: `Remove "${doc.label}"?`,
      message: "This document will be permanently deleted from this payment stage.",
      confirmText: "Remove",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/payment-stages/${stageId}/documents/${doc.doc_id}`);
      toast.success("Removed");
      onChange && (await onChange());
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="mt-4 rounded-2xl border border-[#EBE5D9] bg-[#FBF7EF]/60 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <div className="text-[10px] uppercase tracking-widest font-bold text-[#7A7571]">
          Documents
          {documents.length > 0 && <span className="ml-2 text-[#2C2A29]">({documents.length})</span>}
        </div>
        {canUpload && (
          <div className="flex items-center flex-wrap gap-1">
            <UploadTypeButton onClick={() => startUpload("invoice")} label="Invoice" disabled={uploading} testid={`doc-upload-invoice-${stageId}`} />
            <UploadTypeButton onClick={() => startUpload("receipt")} label="Receipt" disabled={uploading} testid={`doc-upload-receipt-${stageId}`} />
            <UploadTypeButton onClick={() => startUpload("other")} label="Other" disabled={uploading} testid={`doc-upload-other-${stageId}`} />
          </div>
        )}
      </div>

      {documents.length === 0 ? (
        <div className="text-xs text-[#7A7571] italic py-2">
          {uploading
            ? "Uploading…"
            : canUpload
              ? "No documents yet. Upload the invoice, proof of payment, or any related file."
              : "Your ID firm hasn't shared any documents for this stage yet."}
        </div>
      ) : (
        <ul className="space-y-1.5">
          {documents.map((d) => {
            const meta = TYPE_META[d.type] || TYPE_META.other;
            const Icon = meta.icon;
            const canDelete = canDeleteAny;
            return (
              <li
                key={d.doc_id}
                className="flex items-center gap-2 rounded-xl bg-white border border-[#EBE5D9] px-3 py-2"
                data-testid={`payment-doc-${d.doc_id}`}
              >
                <span className={`w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 ${meta.tint}`}>
                  <Icon size={14} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-[#2C2A29] truncate">{d.label || d.filename}</span>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${meta.tint}`}>
                      {meta.label}
                    </span>
                  </div>
                  <div className="text-[11px] text-[#7A7571] truncate">
                    {d.uploaded_by_role === "customer" ? "Homeowner" : (d.uploaded_by_name || "ID firm")}
                    {" · "}
                    {new Date(d.uploaded_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}
                    {d.size ? ` · ${fmtSize(d.size)}` : ""}
                  </div>
                </div>
                <a
                  href={`${api.defaults.baseURL}/payment-stages/${stageId}/documents/${d.doc_id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-ghost !p-1.5 text-[#7A7571] hover:text-[#2C2A29]"
                  title="Download"
                  data-testid={`payment-doc-${d.doc_id}-download`}
                >
                  <Download size={14} />
                </a>
                {canDelete && (
                  <button
                    onClick={() => remove(d)}
                    className="btn-ghost !p-1.5 text-[#9F1239]"
                    title="Remove"
                    data-testid={`payment-doc-${d.doc_id}-delete`}
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/*,application/pdf"
        className="hidden"
        onChange={(e) => onFile(e.target.files?.[0])}
      />
    </div>
  );
}

function UploadTypeButton({ onClick, label, disabled, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full border border-[#EBE5D9] bg-white text-[#2C2A29] hover:bg-[#F7F4EC] disabled:opacity-60"
      title={`Upload ${label}`}
    >
      {disabled ? <Loader2 size={11} className="animate-spin" /> : <Upload size={11} />} {label}
    </button>
  );
}
